import { useMemo, useState } from "react";
import { CaretUp, CaretDown, CaretUpDown } from "@phosphor-icons/react";

/**
 * Generic sortable table.
 *
 * columns: [{ key, label, align: 'left'|'right'|'center', type: 'text'|'number'|'date',
 *             sortable=true, render(row)=>node, className, thClassName, width }]
 * defaultSort: [{ key, dir: 'asc'|'desc' }]  (multi-key until the user clicks a header)
 */
export default function SortableTable({
  columns, rows, defaultSort = [], rowKey = "id", onRowClick, testIdPrefix = "row", empty = "No data.",
}) {
  const [userSort, setUserSort] = useState(null); // {key, dir}

  const effective = userSort ? [userSort] : defaultSort;

  const cmpFor = (type) => (a, b) => {
    if (a == null && b == null) return 0;
    if (a == null) return 1; // nulls last
    if (b == null) return -1;
    if (type === "number") return Number(a) - Number(b);
    if (type === "date") return String(a).localeCompare(String(b));
    return String(a).localeCompare(String(b), undefined, { sensitivity: "base", numeric: true });
  };

  const sorted = useMemo(() => {
    const colByKey = Object.fromEntries(columns.map((c) => [c.key, c]));
    const arr = [...(rows || [])];
    arr.sort((ra, rb) => {
      for (const s of effective) {
        const col = colByKey[s.key] || {};
        const base = cmpFor(col.type || "text")(ra[s.key], rb[s.key]);
        if (base !== 0) return s.dir === "asc" ? base : -base;
      }
      return 0;
    });
    return arr;
  }, [rows, columns, effective]);

  const toggle = (col) => {
    if (col.sortable === false) return;
    setUserSort((prev) => {
      if (!prev || prev.key !== col.key) return { key: col.key, dir: col.type === "text" ? "asc" : "desc" };
      return { key: col.key, dir: prev.dir === "asc" ? "desc" : "asc" };
    });
  };

  const arrowFor = (col) => {
    const active = (userSort && userSort.key === col.key) || (!userSort && defaultSort[0]?.key === col.key);
    const dir = userSort && userSort.key === col.key ? userSort.dir : (!userSort && defaultSort[0]?.key === col.key ? defaultSort[0].dir : null);
    if (col.sortable === false) return null;
    if (!active) return <CaretUpDown size={12} weight="bold" className="opacity-30" />;
    return dir === "asc" ? <CaretUp size={12} weight="bold" /> : <CaretDown size={12} weight="bold" />;
  };

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm border-collapse" data-testid="sortable-table">
        <thead>
          <tr className="bg-secondary/40 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
            {columns.map((c) => (
              <th
                key={c.key}
                data-testid={`th-${c.key}`}
                onClick={() => toggle(c)}
                style={c.width ? { width: c.width } : undefined}
                className={`px-3 py-2.5 whitespace-nowrap ${c.align === "right" ? "text-right" : c.align === "center" ? "text-center" : "text-left"} ${c.sortable === false ? "" : "cursor-pointer select-none hover:text-riq-primary"} ${c.thClassName || ""}`}
              >
                <span className={`inline-flex items-center gap-1 ${c.align === "right" ? "flex-row-reverse" : ""}`}>
                  {c.label} {arrowFor(c)}
                </span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-riq-border">
          {sorted.length === 0 ? (
            <tr><td colSpan={columns.length} className="py-10 text-center text-muted-foreground">{empty}</td></tr>
          ) : sorted.map((row) => (
            <tr
              key={row[rowKey]}
              data-testid={`${testIdPrefix}-${row[rowKey]}`}
              onClick={onRowClick ? () => onRowClick(row) : undefined}
              className={`${onRowClick ? "cursor-pointer" : ""} hover:bg-secondary/40 transition-colors`}
            >
              {columns.map((c) => (
                <td
                  key={c.key}
                  className={`px-3 py-2.5 align-middle ${c.align === "right" ? "text-right" : c.align === "center" ? "text-center" : "text-left"} ${c.className || ""}`}
                >
                  {c.render ? c.render(row) : row[c.key]}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
